export const HIDDEN_CLIENT_VALUE = '*';

export const CLIENT_IDENTITY_FIELDS = [
	'userName',
	'username',
	'firstName',
	'firstname',
	'lastName',
	'lastname',
] as const;
export const CLIENT_MOBILE_FIELDS = ['mobile', 'mobileNumber'] as const;
export const LEAD_MOBILE_FIELDS = ['mobile', 'mobileNumber'] as const;

export type VisibilityPermission = boolean | Boolean | null | undefined;

export interface ClientVisibilityPermissions {
	canSeeClientNames?: VisibilityPermission;
	canSeeMobileNumbers?: VisibilityPermission;
}

type ClientIdentityField = typeof CLIENT_IDENTITY_FIELDS[number];
type ClientMobileField = typeof CLIENT_MOBILE_FIELDS[number];
type LeadMobileField = typeof LEAD_MOBILE_FIELDS[number];
type VisibilityValue = string | number | undefined | null;

export interface ClientVisibilityUser {
	userType?: number | string | null;
	userTypeId?: number | string | null;
	userName?: VisibilityValue;
	username?: VisibilityValue;
	firstName?: VisibilityValue;
	firstname?: VisibilityValue;
	lastName?: VisibilityValue;
	lastname?: VisibilityValue;
	mobile?: VisibilityValue;
	mobileNumber?: VisibilityValue;
}

export interface LeadVisibilityRecord {
	mobile?: VisibilityValue;
	mobileNumber?: VisibilityValue;
}

const normalizeUserType = (userType: number | string | undefined | null) => Number(userType);

const isClientVisibilityPermissions = (
	value: VisibilityPermission | ClientVisibilityPermissions,
): value is ClientVisibilityPermissions =>
	typeof value === 'object' && value !== null && !(value instanceof Boolean);

const maskObjectFields = <T extends object>(source: T, fields: readonly string[]): T => {
	const result = { ...source } as T;
	const resultRecord = result as Record<string, unknown>;
	const sourceRecord = source as Record<string, unknown>;

	fields.forEach((field) => {
		if (Object.prototype.hasOwnProperty.call(sourceRecord, field)) {
			resultRecord[field] = HIDDEN_CLIENT_VALUE;
		}
	});

	return result;
};

export const isClientOrOfficeUserType = (userType?: number | string | null) => {
	const normalizedUserType = normalizeUserType(userType);
	return normalizedUserType === 1 || normalizedUserType === 2;
};

export const shouldHideClientIdentity = (
	canSeeClientNames: VisibilityPermission,
	userType?: number | string | null,
) => canSeeClientNames !== true && isClientOrOfficeUserType(userType);

export const shouldHideClientMobile = (
	canSeeMobileNumbers: VisibilityPermission,
	userType?: number | string | null,
) => canSeeMobileNumbers !== true && isClientOrOfficeUserType(userType);

export const shouldHideLeadMobile = (canSeeLeadsMobile: VisibilityPermission) =>
	canSeeLeadsMobile !== true;

export const maskClientIdentityValue = (
	value: VisibilityValue,
	canSeeClientNames: VisibilityPermission,
	userType?: number | string | null,
) => {
	if (shouldHideClientIdentity(canSeeClientNames, userType)) {
		return HIDDEN_CLIENT_VALUE;
	}

	return value == null ? '' : String(value);
};

export const maskClientMobileValue = (
	value: VisibilityValue,
	canSeeMobileNumbers: VisibilityPermission,
	userType?: number | string | null,
) => {
	if (shouldHideClientMobile(canSeeMobileNumbers, userType)) {
		return HIDDEN_CLIENT_VALUE;
	}

	return value == null ? '' : String(value);
};

export const maskLeadMobileValue = (
	value: VisibilityValue,
	canSeeLeadsMobile: VisibilityPermission,
) => {
	if (shouldHideLeadMobile(canSeeLeadsMobile)) {
		return HIDDEN_CLIENT_VALUE;
	}

	return value == null ? '' : String(value);
};

export function maskClientUser<T extends ClientVisibilityUser>(
	user: T,
	permissions: ClientVisibilityPermissions,
): T;
export function maskClientUser<T extends ClientVisibilityUser>(
	user: T,
	canSeeClientNames: VisibilityPermission,
	canSeeMobileNumbers?: VisibilityPermission,
): T;
export function maskClientUser<T extends ClientVisibilityUser>(
	user: T,
	permissionsOrCanSeeClientNames: VisibilityPermission | ClientVisibilityPermissions,
	canSeeMobileNumbers?: VisibilityPermission,
): T {
	const usesPermissionObject = isClientVisibilityPermissions(permissionsOrCanSeeClientNames);
	const permissions = usesPermissionObject
		? permissionsOrCanSeeClientNames
		: {
				canSeeClientNames: permissionsOrCanSeeClientNames,
				canSeeMobileNumbers,
		  };
	const userType = user.userType ?? user.userTypeId;
	let maskedUser = { ...user };

	if (shouldHideClientIdentity(permissions.canSeeClientNames, userType)) {
		maskedUser = maskObjectFields(maskedUser, CLIENT_IDENTITY_FIELDS);
	}

	// The primitive two-argument form is retained for existing identity-only callers.
	// Passing the policy object (preferred) or an explicit third argument enables mobile masking.
	if (
		(usesPermissionObject || arguments.length >= 3) &&
		shouldHideClientMobile(permissions.canSeeMobileNumbers, userType)
	) {
		maskedUser = maskObjectFields(maskedUser, CLIENT_MOBILE_FIELDS);
	}

	return maskedUser;
}

export const maskLead = <T extends LeadVisibilityRecord>(
	lead: T,
	canSeeLeadsMobile: VisibilityPermission,
): T =>
	shouldHideLeadMobile(canSeeLeadsMobile)
		? maskObjectFields(lead, LEAD_MOBILE_FIELDS)
		: { ...lead };

export const shouldOmitHiddenClientField = (
	field: string,
	permissions: ClientVisibilityPermissions,
	userType?: number | string | null,
) =>
	(CLIENT_IDENTITY_FIELDS.includes(field as ClientIdentityField) &&
		shouldHideClientIdentity(permissions.canSeeClientNames, userType)) ||
	(CLIENT_MOBILE_FIELDS.includes(field as ClientMobileField) &&
		shouldHideClientMobile(permissions.canSeeMobileNumbers, userType));

export const shouldOmitHiddenLeadField = (field: string, canSeeLeadsMobile: VisibilityPermission) =>
	LEAD_MOBILE_FIELDS.includes(field as LeadMobileField) &&
	shouldHideLeadMobile(canSeeLeadsMobile);

export const getReportUserId = (row: {
	userID?: number | Number | null;
	userId?: number | Number | null;
	userid?: number | Number | null;
}) => {
	const normalized = Number(row.userID ?? row.userId ?? row.userid ?? 0);
	return Number.isFinite(normalized) && normalized > 0 ? normalized : undefined;
};
